import React, { useEffect, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import { formatRubPrice } from "../visoria/helpers";

function formatDate(value) {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleString("ru-RU");
}

function VsOpsSalesPage() {
  const [filters, setFilters] = useState({
    email: "",
    code: "",
    storeId: "",
    regionId: "",
    from: "",
    to: "",
    includePending: false,
  });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [returnInputs, setReturnInputs] = useState({});
  const [returnReasonInputs, setReturnReasonInputs] = useState({});
  const [returnLoading, setReturnLoading] = useState({});
  const [returnMessages, setReturnMessages] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // includePending must be sent ONLY when checked,
      // otherwise backend treats non-empty strings like "false" as truthy.
      const params = Object.fromEntries(
        Object.entries(filters)
          .filter(([key, value]) => {
            if (key === "includePending") {
              return Boolean(value);
            }
            return String(value).trim();
          })
          .map(([key, value]) => [
            key,
            key === "includePending" ? "1" : value,
          ]),
      );
      const result = await vsOpsApi.getSales(params);
      setData(result);
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleReturnCard = async (orderId) => {
    const keyCode = String(returnInputs[orderId] || "").trim();
    if (!keyCode) {
      setReturnMessages((current) => ({
        ...current,
        [orderId]: { type: "error", text: "Вставьте код карты для возврата" },
      }));
      return;
    }

    setReturnLoading((current) => ({ ...current, [orderId]: true }));
    setReturnMessages((current) => ({ ...current, [orderId]: null }));
    try {
      await vsOpsApi.returnCard(orderId, keyCode, returnReasonInputs[orderId] || "");
      setReturnInputs((current) => ({ ...current, [orderId]: "" }));
      setReturnReasonInputs((current) => ({ ...current, [orderId]: "" }));
      setReturnMessages((current) => ({
        ...current,
        [orderId]: { type: "success", text: "Карта возвращена на локальный склад" },
      }));
      await load();
    } catch (returnError) {
      setReturnMessages((current) => ({
        ...current,
        [orderId]: {
          type: "error",
          text: returnError.message || "Не удалось вернуть карту",
        },
      }));
    } finally {
      setReturnLoading((current) => ({ ...current, [orderId]: false }));
    }
  };

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout title="Продажи">
        <form
          className="vs-ops-filters"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <label>
            Email
            <input
              value={filters.email}
              onChange={(event) =>
                setFilters((current) => ({ ...current, email: event.target.value }))
              }
              placeholder="customer@mail.ru"
            />
          </label>
          <label>
            Код
            <input
              value={filters.code}
              onChange={(event) =>
                setFilters((current) => ({ ...current, code: event.target.value }))
              }
              placeholder="XXXX-XXXX"
            />
          </label>
          <label>
            Магазин
            <input
              value={filters.storeId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, storeId: event.target.value }))
              }
              placeholder="nintendo"
            />
          </label>
          <label>
            Регион
            <input
              value={filters.regionId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, regionId: event.target.value }))
              }
              placeholder="usd"
            />
          </label>
          <label>
            С
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters((current) => ({ ...current, from: event.target.value }))
              }
            />
          </label>
          <label>
            По
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters((current) => ({ ...current, to: event.target.value }))
              }
            />
          </label>
          <label className="vs-ops-checkbox">
            <input
              type="checkbox"
              checked={filters.includePending}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  includePending: event.target.checked,
                }))
              }
            />
            Показать неоплаченные счета
          </label>
          <button type="submit" className="vs-ops-btn" disabled={loading}>
            Найти
          </button>
        </form>

        {error ? <p className="vs-ops-error">{error}</p> : null}
        <p className="vs-ops-muted">Найдено: {data.total}</p>

        <div className="vs-ops-table-wrap">
          <table className="vs-ops-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Email</th>
                <th>Магазин</th>
                <th>Регион</th>
                <th>Товар</th>
                <th>Коды</th>
                <th>Выдача</th>
                <th>Возврат / перепродажа</th>
                <th>Сумма</th>
                <th>Прибыль</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.orderId}>
                  <td>{formatDate(item.paymentCompletedAt || item.createdAt)}</td>
                  <td>{item.email}</td>
                  <td>{item.storeId}</td>
                  <td>{item.regionId}</td>
                  <td>
                    {item.productName}
                    <span className="vs-ops-muted"> ×{item.quantity}</span>
                  </td>
                  <td className="vs-ops-codes-cell">
                    {item.keys?.length
                      ? item.keys.map((keyItem) => (
                          <code key={keyItem.key}>{keyItem.key}</code>
                        ))
                      : "—"}
                  </td>
                  <td>
                    <div className="vs-ops-stack">
                      <span>{item.fulfillmentStatus || "—"}</span>
                      {item.fulfillmentError ? (
                        <span className="vs-ops-error">{item.fulfillmentError}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="vs-ops-stack">
                      <input
                        value={returnInputs[item.orderId] || ""}
                        onChange={(event) =>
                          setReturnInputs((current) => ({
                            ...current,
                            [item.orderId]: event.target.value,
                          }))
                        }
                        placeholder="Вставьте код для возврата"
                      />
                      <input
                        value={returnReasonInputs[item.orderId] || ""}
                        onChange={(event) =>
                          setReturnReasonInputs((current) => ({
                            ...current,
                            [item.orderId]: event.target.value,
                          }))
                        }
                        placeholder="Причина возврата (необязательно)"
                      />
                      <button
                        type="button"
                        className="vs-ops-btn"
                        disabled={Boolean(returnLoading[item.orderId])}
                        onClick={() => handleReturnCard(item.orderId)}
                      >
                        Вернуть
                      </button>
                      {returnMessages[item.orderId]?.text ? (
                        <p
                          className={
                            returnMessages[item.orderId]?.type === "error"
                              ? "vs-ops-error"
                              : "vs-ops-muted"
                          }
                        >
                          {returnMessages[item.orderId].text}
                        </p>
                      ) : null}
                      {item.hasReturnedCards ? (
                        <div className="vs-ops-muted">
                          Возвращено: {item.returnedCardCount}
                          {item.returnedKeys?.map((returned) => (
                            <div key={returned.id}>
                              <code>{returned.keyCode}</code>
                              {returned.resoldOrderId
                                ? ` → перепродана в ${returned.resoldOrderId}`
                                : " → на локальном складе"}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {item.resoldFromOrderId ? (
                        <p className="vs-ops-muted">
                          Карта из возврата заказа {item.resoldFromOrderId}
                        </p>
                      ) : null}
                    </div>
                  </td>
                  <td>{formatRubPrice(item.profit.amountRub)}</td>
                  <td>{formatRubPrice(item.profit.profitRub)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsSalesPage;
